import { Component, MouseEvent, ReactNode } from 'react';
import TransformableInputArea from '../TransformableInputArea';
import { EnrichedComponentCategory } from '../../domain/category/CategoryOperationDomains';
import CustomOperationComponent from '../custom/CustomOperationComponent';
import GatewayContainer from '../../config/GatewayContainer';
import GatewayNames from '../../constant/GatewayNames';
import ComponentName from '../../constant/ComponentName';
import FolderClient from 'client/folder/FolderClient';
import Modal from './view/Modal';
import ItemView from './view/ItemView';
import MessageUtil from '../../util/MessageUtil';
import RoleHelper from '../../RoleHelper';
import ComponentCategoryUtil from '../../util/ComponenyCategoryUtil';
import { AccessBase } from '../../domain/access/BaseAccess';
import moment from 'moment/moment';
import { PageState } from '../../redux/reducers/PageReducer';
import ComponentService from '../../service/ComponentService';
import ServiceContainer from '../../config/ServiceContainer';
import { ServiceNames } from '../../constant/ServiceNames';
import LanguageUtil from 'util/LanguageUtil';

class FolderItem extends Component<Props, State> {
  private folderClient: FolderClient = GatewayContainer.getInstance().get(GatewayNames.FOLDER_CLIENT);
  private componentService: ComponentService = ServiceContainer.getInstance().get(ServiceNames.COMPONENT);

  public state: State = new State();

  public render(): ReactNode {
    const { category, view, selectedFolders, component } = this.props;
    const { selected } = this.state;

    let id = null;
    if (view) {
      id = getItemTypeWithId(view.getModel());
    }

    if (!(id && category && view && category.components)) {
      return null;
    }

    return (
      <div
        data-testid={`folder-item-${category.name}`}
        className={`flex flex-col border-2 rounded-[4px] border-f-blue-2 ${selected ? 'border-opacity-100 bg-f-blue' : 'border-opacity-0'}`}
      >
        <div className={'flex flex-col group'} id={`parent_folder_${view.getModel().id}`}>
          <div
            key={`${view.getModel().id}`}
            id={`${id}_${view.getModel().id}`}
            onClick={(e) => this.onSelectedChange(e)}
            onDoubleClick={this.onDoubleClick}
            className={`w-[120px] h-[100px] ${selected && 'border-l border-r'} rounded-[4px] relative folder-main cursor-pointer`}
          >
            <div className={'text-2xs px-1 py-1 text-f-blue-3 absolute top-0 left-1 select-none'}>{category.components.length} Files</div>
            {selectedFolders?.length > 0 && (
              <div
                id={`folder_${view.getModel().value?.payload.id}_label`}
                className={'absolute -top-2 -right-2 bg-[#CC0000] rounded-full text-white w-4 h-4 flex items-center justify-center opacity-0'}
              >
                {selectedFolders.length}{' '}
              </div>
            )}
            <div
              id={`folder_to_target_folder_${view.getModel().value?.payload.id}_label`}
              className={
                'absolute -bottom-[25px] -right-[26px] z-10 w-[130px] mt-2 h-4 bg-primary text-center truncate rounded-full text-white flex items-center justify-center opacity-0 '
              }
            ></div>
            <div className={'w-full h-full '}>
              <i className={'absolute top-5 right-0 px-2 hover:cursor-pointer fill-f-black-3 hover:fill-f-black-3'}>
                {RoleHelper.canEdit(ComponentCategoryUtil.getRoleNamesByComponent(component)) && (
                  <CustomOperationComponent
                    items={[
                      {
                        title: LanguageUtil.get({ id: 'common.label.remove', defaultMessage: 'Remove' }),
                        onClick: this.deleteCategory,
                      },
                      {
                        title: LanguageUtil.get({ id: 'common.label.export', defaultMessage: 'Export' }),
                        onClick: this.exportCategory,
                      },
                    ]}
                  />
                )}
              </i>
            </div>
            <TransformableInputArea
              className={
                '!h-[27px] select-none px-2 bg-transparent  flex justify-center items-center text-center w-full absolute bottom-0 truncate  hover:h-10 text-f-black-2  text-2xs transition-all'
              }
              value={category.name}
              disabled={!RoleHelper.canEdit(ComponentCategoryUtil.getRoleNamesByComponent(component))}
              onClose={this.changeCategoryName}
              onVisible={() => this.setState({ categoryChangeVisible: true })}
            >
              {category.name}
            </TransformableInputArea>
          </div>
        </div>
      </div>
    );
  }

  public componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any) {
    const { selectedFolders, view } = this.props;
    if (prevProps.selectedFolders != selectedFolders) {
      const isSelected = selectedFolders.some((f) => f.id == view?.getModel().value?.payload.id);
      this.setState({ selected: isSelected });
    }
  }

  private deleteCategory = async () => {
    const { category, component, selectedFolders, setSelectedFolders } = this.props;
    if (!RoleHelper.canEdit(ComponentCategoryUtil.getRoleNamesByComponent(component))) return;
    try {
      if (selectedFolders.length > 0) {
        const deletingFolders = selectedFolders.filter((f) => f.value?.payload.components?.length == 0 && f.value?.payload.categories?.length == 0);
        if (deletingFolders.length > 0) {
          await Promise.all(deletingFolders.map((f) => this.folderClient.delete(component, f.value?.payload.id)));
          MessageUtil.success({
            summary: 'Folders Deleted',
          });
        }
        setSelectedFolders([]);
      } else {
        if (category.components.length > 0 || category.categories.length > 0) {
          MessageUtil.error({
            summary: 'Folder is not empty',
          });
          return;
        }
        await this.folderClient.delete(component, category.id);
        MessageUtil.success({
          summary: `Folder Deleted`,
        });
      }
    } catch (e) {}
  };

  private getAllComponentIds = (category: EnrichedComponentCategory): string[] => {
    let components: string[] = [...(category.components || [])];
    if (category.categories && category.categories.length > 0) {
      category.categories.forEach((subCategory) => {
        components = [...components, ...this.getAllComponentIds(subCategory)];
      });
    }
    return components;
  };

  private exportCategory = async () => {
    const { category, component, page } = this.props;
    try {
      const exportComponents = this.getAllComponentIds(category);

      if (exportComponents.length > 0) {
        const currentCategory = page.currentCategory?.id;
        const data: AccessBase[] = await this.componentService.export(component, exportComponents, currentCategory);

        if (data.length === 0) {
          MessageUtil.info({
            summary: 'No items to export',
          });
          return;
        }

        const aElement = document.createElement('a');
        const blob = new Blob([JSON.stringify(data)], { type: 'application/json' });
        aElement.href = URL.createObjectURL(blob);
        aElement.download = `${component}_${moment().valueOf()}.json`;
        aElement.click();
        aElement.remove();
        MessageUtil.success({
          summary: 'Export completed successfully',
        });
      } else {
        MessageUtil.info({
          summary: 'No components found to export',
        });
      }
    } catch (e) {
      console.error(e);
      MessageUtil.error({
        summary: 'Export failed',
        content: 'An error occurred while exporting',
      });
    }
  };

  private onSelectedChange = (e: any) => {
    const { selectedFolders, category, setSelectedFolders, view } = this.props;
    const { selected } = this.state;
    this.setState({ selected: !selected }, () => {
      if (!selected && selectedFolders && view) {
        selectedFolders.push(view.getModel());
        setSelectedFolders(selectedFolders);
      } else if (selected && selectedFolders && view) {
        const index = selectedFolders.findIndex((item) => item.value?.payload.id != category.id);
        if (index != -1) {
          selectedFolders.splice(index, 1);
        }
        setSelectedFolders(selectedFolders);
      }
    });
  };

  private changeCategoryName = async (value: string) => {
    const { category, component } = this.props;
    this.setState({ categoryChangeVisible: false });
    try {
      if (value.trim().length != 0) {
        if (category.name != value) {
          await this.folderClient.updateName(component, category.id, value);
          MessageUtil.success({ content: 'Folder name changed successfully' });
        }
      }
    } catch (e) {
      console.error(e);
    }
  };

  private onDoubleClick = (e: MouseEvent) => {
    const { categoryChangeVisible } = this.state;
    if (categoryChangeVisible) return;
    try {
      const closest = (e.target as HTMLInputElement).closest('.transformable-input-area');
      if (closest != null) {
        return;
      }
    } catch (e) {
      console.error(e);
    }
    const { onClick } = this.props;
    e.preventDefault();
    e.stopPropagation();
    onClick();
  };
}

class State {
  value: string | undefined;
  selected: boolean = false;
  categoryChangeVisible: boolean = false;
}

interface Props {
  component: ComponentName;
  category: EnrichedComponentCategory;
  onClick: () => void;
  view?: ItemView;
  selectedFolders: Modal[];
  setSelectedFolders: (folders: Modal[]) => void;
  page: PageState;
}

export const getItemTypeWithId = (model: any) => {
  let id;
  if (model.value?.type == 'Folder') {
    id = `folder`;
  } else if (model.value?.type == 'List') {
    id = `list`;
  } else {
    id = `undefined`;
  }
  return id;
};

export default FolderItem;
